<template>
    <div class="address-container">
        <text-input
            label="Street number"
            type="text"
            :value="deliveryAddress"
            autocomplete="address-line1"
            @change="updatePredictions($event)"
            @blur="validateAddress"
            :error-message="errorMessage"
        ></text-input>
        <div class="predictions-list" v-if="isOpen">
            <li v-for="(prediction, i) in predictions" @mousedown="setPrediction(prediction)" :key="i">
                {{ prediction.description }}
            </li>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import Component from 'vue-class-component';
import { Model, Prop, Watch} from 'vue-property-decorator';
import TextInput from './TextInput.vue';
import Settings, { SettingNamespace } from '@/utils/settings';
import { AddressStatusResponse } from '@/utils/types';
@Component({
    components: {TextInput}
})
export default class AddressField extends Vue
{
    @Model('input', {type: String}) readonly deliveryAddress!: string;
    @Prop(String) readonly storePlaceId!: string;
    @Prop(Number) readonly maxDeliveryDistance!: number;
    @Prop(String) readonly country!: string;
    @Prop(String) readonly errorMessage!: string;
    @Prop(String) readonly apiKey!: string;
    @Prop(String) readonly addressPlaceId!: string;

    autocompleteService!: google.maps.places.AutocompleteService;
    distanceMatrixService!: google.maps.DirectionsService;
    geocoder!: google.maps.Geocoder;
    predictions: google.maps.places.AutocompletePrediction[] = [];
    sessionToken: google.maps.places.AutocompleteSessionToken = '';
    isOpen: boolean = false;
    showStreetNumber: boolean = false;
    streetNumber: string = '';
    isGoogleLoaded = false;
    selectedPrediction: google.maps.places.AutocompletePrediction | null = null;
    travelMode!: google.maps.TravelMode;

    async mounted() {
        const loader = new Loader({
            apiKey: this.apiKey,
            version: "weekly",
            libraries: ["places"]
        });
        const settings = await Settings.getInstance();
        const google = await loader.load();
        this.autocompleteService = new google.maps.places.AutocompleteService();
        this.distanceMatrixService = new google.maps.DirectionsService();
        this.geocoder = new google.maps.Geocoder();
        this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        const travelMode = settings.get(SettingNamespace.ORDERS, 'travel_mode')?.value;
        this.travelMode = (travelMode && travelMode === google.maps.TravelMode.WALKING) ? google.maps.TravelMode.WALKING : google.maps.TravelMode.DRIVING;
        this.updateIsOpen();
        await this.validateAddress();
    }

    @Watch('predictions')
    updateIsOpen() {
        this.isOpen = !!this.predictions.length;
    }

    set inputValue(value: string) {
        this.$emit('change', value);
    }

    get inputValue() {
        return this.deliveryAddress;
    }

    async updatePredictions(input: string) {
        if (input) {
            const response = await this.autocompleteService.getPlacePredictions({
                input: input,
                componentRestrictions: {
                    country: this.country
                },
                sessionToken: this.sessionToken
            });
            if (response.predictions.length) {
                return this.predictions = response.predictions;
            }
            return this.predictions = [];
        }
    }

    async validateAddress() {
        this.isOpen = false;
        if (this.selectedPrediction) {
            const geocodeResult = await this.geocodePlaceId(this.selectedPrediction.place_id);
            if (geocodeResult) {
                this.inputValue = this.isAddressMalformed(geocodeResult) ? this.selectedPrediction.description : geocodeResult.formatted_address;
                const distanceInMiles = await this.getDistanceBetweenPlaceIds(this.storePlaceId, this.selectedPrediction.place_id);
                return this.emitAddressValidation(distanceInMiles);
            }
            this.inputValue = this.selectedPrediction.description;
            return this.$emit('addressValid', { success: false, status: AddressStatusResponse.NOT_FOUND });
        }
        const geocodeAddress = await this.geocodeAddress(this.inputValue);
        if (geocodeAddress) {
            const distanceInMiles = await this.getDistanceBetweenPlaceIds(this.storePlaceId, geocodeAddress.place_id);
            return this.emitAddressValidation(distanceInMiles);
        }
        return this.$emit('addressValid', { success: false, status: AddressStatusResponse.NOT_FOUND});
    }

    emitAddressValidation(distanceInMiles: number | false) {
        if (distanceInMiles) {
            const isAddressValid = distanceInMiles <= this.maxDeliveryDistance;
            if (isAddressValid) {
                this.$emit('onPredictionUpdate', this.selectedPrediction?.place_id || '');
                this.$emit('addressValid', { success: true, content: distanceInMiles });
            } else {
                this.$emit('addressValid', { success: false, status: AddressStatusResponse.EXCEEDED_DISTANCE });
            }
        } else {
            this.$emit('addressValid', { success: false, status: AddressStatusResponse.NOT_FOUND });
        }
    }

    get addressContainsPlusCode() {
        return (geocoderResult: google.maps.GeocoderResult) => {
            const plusCode = geocoderResult.address_components.find((component) => component.types[0] === 'plus_code');
            return !!plusCode;
        };
    }

    get addressContainsNumber() {
        return (address: string) => /\d/.test(address);
    }

    get isAddressMalformed() {
        return (geocodeResult: google.maps.GeocoderResult) => geocodeResult.types[0] === 'route' || this.addressContainsPlusCode(geocodeResult) || !this.addressContainsNumber(geocodeResult.formatted_address);
    }

    async geocodeAddress(address: string) {
        try {
            const response = await this.geocoder.geocode({ address: address, region: 'KY' });
            if (response.results[0].formatted_address !== 'Cayman Islands' && response.results[0].geometry.location_type !== google.maps.GeocoderLocationType.APPROXIMATE) {
                return response.results[0];
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    async geocodePlaceId(placeId: string) {
        try {
            const response = await this.geocoder.geocode({ placeId: placeId });
            if (response.results[0].formatted_address !== 'Cayman Islands' && response.results[0].geometry.location_type !== google.maps.GeocoderLocationType.APPROXIMATE) {
                return response.results[0];
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    async getDistanceBetweenPlaceIds(storePlaceId: string, destinationPlaceId: string) {
        const request = {
            origin: { 'placeId': storePlaceId },
            destination: { 'placeId': destinationPlaceId },
            travelMode: this.travelMode,
            unitSystem: google.maps.UnitSystem.IMPERIAL
        };
        try {
            const response = await this.distanceMatrixService.route(request);
            if (response.routes[0].legs[0].distance) {
                return response.routes[0].legs[0].distance.value * 0.000621371;
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    setPrediction(prediction: google.maps.places.AutocompletePrediction) {
        this.selectedPrediction = prediction;
    }
}

</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.address-container {
    position: relative;

    .predictions-list {
        position: absolute;
        box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);
        border: 1px solid var(--medium-gray);
        border-radius: var(--global-radius);
        z-index: 10;
        background: white;
        font-weight: 500;
        top: 60px;
        width: 100%;

        li {
            padding: 15px 10px;
            list-style-type: none;
            color: var(--dark-gray);
            padding-bottom: 10px;
            cursor: pointer;

            &:not(:last-child) {
                border-bottom: 1px solid var(--light-gray);
            }

            &:hover {
                background-color: var(--almost-white);
            }
        }
    }
}
</style>
